import React, { useEffect, useRef, useState } from "react";
import { Link, scrollSpy } from "react-scroll";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ServicesModal from "../modals/ServiceModal";

import LOGO from "../../assets/images/MePatrick.webp";
import { MENU_LINKS } from "../../utils/data";

interface NavbarProps {
  className?: string;
}

// ─── Shiny Services Button ────────────────────────────────────────────────────
// Gradient animates on hover by shifting background-position to top-right.
// All styling is pure Tailwind — no extra dependencies needed.
function ServicesButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-full border-none
        bg-[linear-gradient(325deg,#0044ff_0%,#2ccfff_55%,#0044ff_90%)]
        bg-[length:280%_auto]
        px-5 py-2 text-sm font-medium text-white
        shadow-[0px_0px_20px_rgba(71,184,255,0.5),0px_5px_5px_-1px_rgba(58,125,233,0.25),inset_4px_4px_8px_rgba(175,230,255,0.5),inset_-4px_-4px_8px_rgba(19,95,216,0.35)]
        transition-[background-position] duration-700
        hover:bg-[right_top]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1
        ${className}`}
    >
      Services
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const Navbar: React.FC<NavbarProps> = ({ className = "" }) => {
  const isDark = true;

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (navbarRef.current) {
        gsap.fromTo(
          navbarRef.current,
          { y: -100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.2,
            ease: "back.out(1.7)",
            delay: 1.7,
          },
        );
      }
    },
    { scope: containerRef },
  );

  useGSAP(() => {
    if (!isMobile || !mobileMenuRef.current) return;

    if (isOpen) {
      gsap.to(mobileMenuRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: "power2.out",
        display: "block",
      });
    } else {
      gsap.to(mobileMenuRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          if (mobileMenuRef.current)
            mobileMenuRef.current.style.display = "none";
        },
      });
    }
  }, [isOpen, isMobile]);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  useEffect(() => {
    // Force react-scroll to calculate active links immediately on load
    scrollSpy.update();

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobile(false);
        setIsOpen(false);
      } else {
        setIsMobile(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div ref={containerRef}>
      <nav
        ref={navbarRef}
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center w-full ${className}`}
        style={{ opacity: 20 }}
      >
        <div
          className={`relative flex items-center justify-between w-full max-w-6xl mx-4 mt-5 rounded-full backdrop-blur-[10px] px-6 py-2 shadow-lg transition-colors duration-300 border ${
            isDark ? "bg-white/15 border-white" : "bg-white/25 border-[#34abe1]"
          }`}
        >
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer">
            <img className="h-10 w-auto" src={LOGO} alt="Logo" />
          </div>

          {/* Desktop nav */}
          {!isMobile && (
            <ul className="flex items-center gap-8">
              {MENU_LINKS.map((item: any) => (
                <li key={item.id} className="relative group">
                  <Link
                    activeClass="text-primary font-bold"
                    to={item.to}
                    smooth
                    spy
                    offset={item.offset}
                    className="text-sm font-medium text-base-content/80 transition-colors cursor-pointer hover:text-primary"
                  >
                    {item.label}
                  </Link>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </li>
              ))}

              {/* ✨ Shiny Services button */}
              <ServicesButton
                className="w-max"
                onClick={() => setIsServicesModalOpen(true)}
              />
            </ul>
          )}

          {/* Mobile hamburger */}
          <div className="flex items-center gap-4">
            {isMobile && (
              <button
                className="text-base-content focus:outline-none transition-transform active:scale-90"
                onClick={toggleMenu}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16m-7 6h7"
                    />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile dropdown */}
        {isMobile && (
          <div
            ref={mobileMenuRef}
            style={{ display: "none", opacity: 0 }}
            className={`absolute top-20 left-0 right-0 mx-4 rounded-xl backdrop-blur-md shadow-2xl overflow-hidden border ${
              isDark
                ? "bg-black/95 border-white/10"
                : "bg-white/95 border-[#fee6cc]"
            }`}
          >
            <ul className="flex flex-col py-2">
              {/* ✨ Shiny Services button — mobile */}
              <div className="px-4 py-2">
                <ServicesButton
                  onClick={() => {
                    setIsServicesModalOpen(true);
                    closeMenu();
                  }}
                  className="w-full"
                />
              </div>

              {MENU_LINKS.map((item: any) => (
                <li key={item.id} className="relative">
                  <Link
                    activeClass="text-primary font-bold bg-base-content/5"
                    to={item.to}
                    smooth
                    spy
                    offset={item.offset}
                    onClick={closeMenu}
                    className="block text-base font-medium text-base-content/80 transition-colors cursor-pointer hover:text-primary hover:bg-base-content/10 px-8 py-4"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      <ServicesModal
        isOpen={isServicesModalOpen}
        onClose={() => setIsServicesModalOpen(false)}
      />
    </div>
  );
};

export default Navbar;
